import {
  AttachMoneyOutlined,
  PlayArrowOutlined,
  PlaylistAddOutlined,
  RestartAltOutlined,
  StarBorderOutlined,
} from '@mui/icons-material';
import { ProcessStepItem } from '@procureai/ui-mui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Union } from 'ts-toolbelt';

import { TsrctDemandProcessStep, TsrctDemandProcessStepVariant } from '@/generated/gql/graphql';
import { Action, Target, useTrackInteraction } from '~/hooks/useTrackInteraction/useTrackInteraction';
import { ModuleHash, ModuleHashAction } from '~/modules/Module.types';
import { RouteId, RouteIdPart } from '~/routes';

import { StartAutonomousSourcingDescription } from './components/StartAutonomousSourcingDescription/StartAutonomousSourcingDescription';

export interface UseDemandProcessStepsButtonProps {
  demandId: string;
  steps?: Union.Nullable<TsrctDemandProcessStep[]>;
}

export interface UseDemandProcessStepsButtonResult {
  steps?: ProcessStepItem[];
  batchReleaseOpen: boolean;
  batchReleaseToggle: () => void;
}

export const useDemandProcessStepsButton = ({
  steps,
  demandId,
}: UseDemandProcessStepsButtonProps): UseDemandProcessStepsButtonResult => {
  const navigate = useNavigate();
  const track = useTrackInteraction();
  const { t } = useTranslation(undefined, { keyPrefix: 'DemandProcessStepVariant' });
  const [batchReleaseOpen, setBatchReleaseOpen] = useState(false);
  const location = useLocation();

  const itemConfig = useMemo(() => {
    return {
      [TsrctDemandProcessStepVariant.FINALISE_REQUEST_ANALYSIS]: {
        icon: <PlaylistAddOutlined />,
        onClick: () => {
          track(Target.DEMAND_PROCESS_STEP, Action.CLICKED, {
            action: TsrctDemandProcessStepVariant.FINALISE_REQUEST_ANALYSIS,
          });

          navigate({
            pathname: RouteId.demandDetail(demandId, RouteIdPart.demandTabRequest),
            hash: `${ModuleHash.DEMAND_REQUEST}/${ModuleHashAction.EDIT}`,
          });
        },
      },
      [TsrctDemandProcessStepVariant.FINALISE_NEGOTIATION_CASES]: {
        icon: <AttachMoneyOutlined />,
        onClick: () => {
          track(Target.DEMAND_PROCESS_STEP, Action.CLICKED, {
            action: TsrctDemandProcessStepVariant.FINALISE_NEGOTIATION_CASES,
          });
          navigate({
            pathname: RouteId.demandDetail(demandId, RouteIdPart.demandTabSuppliers),
          });
        },
      },
      [TsrctDemandProcessStepVariant.START_AUTONOMOUS_SOURCING]: {
        description: <StartAutonomousSourcingDescription demandId={demandId} />,
        icon: <PlayArrowOutlined />,
        onClick: () => {
          // TODO open modal when implemented
          // navigate({
          //   pathname: RouteId.demandDetail(demandId, RouteIdPart.demandTabSuppliers),
          // });
          track(Target.DEMAND_PROCESS_STEP, Action.CLICKED, {
            action: TsrctDemandProcessStepVariant.START_AUTONOMOUS_SOURCING,
          });

          setBatchReleaseOpen(true);
        },
      },
      [TsrctDemandProcessStepVariant.BIDS_REQUIRE_MANUAL_REVIEW]: {
        icon: <RestartAltOutlined />,
        onClick: () => {
          track(Target.DEMAND_PROCESS_STEP, Action.CLICKED, {
            action: TsrctDemandProcessStepVariant.BIDS_REQUIRE_MANUAL_REVIEW,
          });
          navigate({
            pathname: RouteId.demandDetail(demandId, RouteIdPart.demandTabSuppliers),
          });
        },
      },
      [TsrctDemandProcessStepVariant.AWARD_DEMAND_TO_SUPPLIER]: {
        icon: <StarBorderOutlined />,
        onClick: () => {
          track(Target.DEMAND_PROCESS_STEP, Action.CLICKED, {
            action: TsrctDemandProcessStepVariant.AWARD_DEMAND_TO_SUPPLIER,
          });
          navigate({
            pathname: RouteId.demandDetail(demandId, RouteIdPart.demandTabAwarding),
          });
        },
      },
    } as Record<TsrctDemandProcessStepVariant, ProcessStepItem>;
  }, [demandId, navigate, track]);

  const items = useMemo(() => {
    if (steps && steps.length) {
      return steps.map((step) => {
        const config = itemConfig[step.variant];
        return {
          ...config,
          title: t(step.variant),
          status: step.status,
        };
      }) as ProcessStepItem[];
    }
    return [];
  }, [t, steps, itemConfig]);

  const batchReleaseToggle = useCallback(() => {
    setBatchReleaseOpen((prev) => !prev);
  }, []);

  // if navigation changes, close the dialog
  useEffect(() => {
    if (batchReleaseOpen) {
      setBatchReleaseOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  return {
    steps: items,
    batchReleaseOpen,
    batchReleaseToggle,
  };
};
